import { apiGet } from '@/lib/api';

/** Promoção ativa do plano. Só vem preenchida quando é mais barata que o preço de catálogo. */
export interface PlanPromotion {
  id: string;
  name: string;
  audience: 'new_subscribers' | 'everyone';
  /** ISO date. */
  ends_at: string;
}

/** Plano vindo de `GET /api/public/plans` (platform_plans + pricing calculado). */
export interface PublicPlan {
  id: string;
  code: string;
  name: string;
  active_quota_monthly: number;
  /** Preço cheio de catálogo. Nunca é sobrescrito pela promoção — use `effectivePlanPriceCents`. */
  monthly_price_cents: number;
  /** Preço promocional já calculado pelo servidor. `null` = sem promoção vigente. */
  promotional_price_cents?: number | null;
  max_users?: number | null;
  max_connections?: number | null;
  extra_seat_monthly_cents?: number | null;
  extra_connection_monthly_cents?: number | null;
  is_custom_quote?: boolean;
  marketing_price_label?: string | null;
  marketing_price_hint?: string | null;
  /** Co-nulo com `promotional_price_cents`. */
  promotion?: PlanPromotion | null;
  is_popular: boolean;
  sort_order: number;
}

export type PlanCode = PublicPlan['code'];

/** Rota de assinatura (checkout pago). */
export const checkoutPath = '/assinar';

export const trialSignupPath = checkoutPath;

export function trialHref(): string {
  return trialSignupPath;
}

export function formatPlanPrice(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * O servidor deriva `is_custom_quote` (enterprise global, sem tenant_id). Não checar `code` aqui:
 * um Enterprise privado de tenant tem preço negociado real e é comprável.
 */
export function isCustomQuotePlan(plan: Pick<PublicPlan, 'is_custom_quote'>): boolean {
  return Boolean(plan.is_custom_quote);
}

/** Preço que o cliente realmente paga. O servidor já aplicou/limitou o desconto. */
export function effectivePlanPriceCents(
  plan: Pick<PublicPlan, 'monthly_price_cents' | 'promotional_price_cents'>,
): number {
  return plan.promotional_price_cents ?? plan.monthly_price_cents;
}

export function hasActivePromotion(plan: PublicPlan): boolean {
  return plan.promotional_price_cents != null && plan.promotion != null;
}

export function formatPlanPriceLabel(plan: PublicPlan): string {
  if (isCustomQuotePlan(plan)) {
    return plan.marketing_price_label ?? 'Sob consulta';
  }
  return formatPlanPrice(effectivePlanPriceCents(plan));
}

export function formatPlanPriceHint(plan: PublicPlan): string | null {
  if (isCustomQuotePlan(plan)) {
    return plan.marketing_price_hint ?? null;
  }
  return 'por mês';
}

/** Menor preço vigente entre os planos compráveis — alimenta o "a partir de" da home. */
export function cheapestPaidPlanPriceCents(plans: PublicPlan[]): number | null {
  const prices = plans
    .filter((p) => !isCustomQuotePlan(p) && effectivePlanPriceCents(p) > 0)
    .map(effectivePlanPriceCents);
  return prices.length > 0 ? Math.min(...prices) : null;
}

export function formatPlanSeats(plan: Pick<PublicPlan, 'max_users'>): string {
  const n = plan.max_users;
  if (n == null) return 'Usuários sob consulta';
  return `${n} usuário${n === 1 ? '' : 's'}`;
}

export function formatPlanChannels(plan: Pick<PublicPlan, 'max_connections'>): string {
  const n = plan.max_connections;
  if (n == null) return 'Canais sob consulta';
  return `${n} ${n === 1 ? 'canal' : 'canais'}`;
}

export function checkoutHref(code: string): string {
  return `${checkoutPath}?plano=${encodeURIComponent(code)}`;
}

export function sortPlans(plans: PublicPlan[]): PublicPlan[] {
  return [...plans].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

export function findPlanByCode(plans: PublicPlan[], code?: string | null): PublicPlan | undefined {
  const key = (code ?? '').toLowerCase();
  if (!key) return undefined;
  return plans.find((p) => p.code.toLowerCase() === key);
}

export function defaultCheckoutPlan(plans: PublicPlan[]): PublicPlan | undefined {
  const sorted = sortPlans(plans).filter((p) => !isCustomQuotePlan(p));
  return sorted.find((p) => p.is_popular) ?? sorted[0];
}

let cachedPlans: PublicPlan[] | null = null;
let inflight: Promise<PublicPlan[]> | null = null;

export function fetchPublicPlans(): Promise<PublicPlan[]> {
  if (cachedPlans) return Promise.resolve(cachedPlans);
  if (!inflight) {
    inflight = apiGet<PublicPlan[]>('/api/public/plans')
      .then((data) => {
        cachedPlans = data;
        return data;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Invalida cache (ex.: após reload forçado). */
export function clearPublicPlansCache(): void {
  cachedPlans = null;
}
